import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import httpInstances from '@/api/axiosInstance';
import { useAppSelector } from '@/hooks/redux-hooks';
import useCodePreview from '@/pages/DataHub/Export/ExportPreview/useCodePreview';
import { useExportSettingsContext } from '@/pages/DataHub/Export/ExportPreview/useExportSettingsContext';
import { RootState } from 'store';

import useDownloadFileContent from '../useDownloadFileContent';

const useJsonCsvPreview = (isCSV: boolean, capturePosthogEvent: () => void) => {
  const { exportType, includeMetadata, from, to, period, periodNumber } =
    useExportSettingsContext();
  const { dataHubs, activeDataHubId } = useAppSelector(state => state.dataHub);
  const { userConfig } = useSelector((state: RootState) => state.userConfig);
  const userTimezone = userConfig.timezone.value;

  const dataHubSymbols = useMemo(() => {
    return dataHubs.find(dataHub => dataHub.id === activeDataHubId)?.symbols || [];
  }, [dataHubs, activeDataHubId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fileContent, setFileContent] = useState<string>();

  const { createRequestToValue, createRequestFromValue } = useCodePreview(
    exportType,
    from,
    to,
    period,
    periodNumber
  );

  const { downloadFileContent } = useDownloadFileContent(capturePosthogEvent);
  const headers = useMemo(() => (isCSV ? { Accept: 'text/csv' } : undefined), [isCSV]);

  const combineCsvData = useCallback((responses: string[]): string => {
    if (!responses.length) return '';

    const [firstResponse, ...otherResponses] = responses;
    const [header, ...rows] = firstResponse.split('\n').filter(Boolean);
    const combinedRows = [...rows];

    for (const response of otherResponses) {
      const [, ...dataRows] = response.split('\n');
      combinedRows.push(...dataRows.filter(Boolean));
    }

    return [header, ...combinedRows].join('\n');
  }, []);

  const getDataHubIndexes = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const requests = dataHubSymbols
        .filter(({ Code }) => !!Code)
        .map(({ Code, Period, PeriodType, TimeRef }) => {
          const fromValue = createRequestFromValue();
          const toValue = createRequestToValue();

          const url = `/index?code=${Code}&period=${Period}&periodType=${PeriodType}&timeRef=${TimeRef}&from=${fromValue}&timezone=${userTimezone}${
            toValue ? `&to=${toValue}` : ''
          }&metadata=${includeMetadata}`;

          return httpInstances.axiosDataInstance.get(url, { headers });
        });

      const results = await Promise.all(requests);

      if (isCSV) {
        const csvData = combineCsvData(results.map(res => res.data));
        setFileContent(csvData);
      } else {
        const flattenedData = results.flatMap(res => res.data);
        setFileContent(JSON.stringify(flattenedData, null, 2));
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [
    dataHubSymbols,
    createRequestFromValue,
    createRequestToValue,
    includeMetadata,
    isCSV,
    userTimezone,
    headers,
    combineCsvData
  ]);

  const downloadFile = useCallback(() => {
    if (fileContent) {
      downloadFileContent(fileContent, isCSV ? 'csv' : 'json');
    }
  }, [fileContent, downloadFileContent, isCSV]);

  useEffect(() => {
    getDataHubIndexes();
  }, [dataHubSymbols, from, to, includeMetadata, period, periodNumber, exportType, isCSV]);

  return { downloadFile, loading, error, fileContent };
};

export default useJsonCsvPreview;
